import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  Grid,
  Chip,
  Paper,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowUpIcon from "@mui/icons-material/ArrowDropUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarIcon from "@mui/icons-material/Star";

import Label from "../../../../components/Label/Label";
import { useNavigate } from "react-router-dom";
import "./index.scss";

const labelStyle = {
  fontSize: 11.5,
  fontWeight: 600,
  color: "#6b7280",
};

const valueStyle = {
  fontSize: 12,
  fontWeight: 500,
  color: "#111827",
};

const HEADERS = [
  "",
  "Name",
  "Version",
  "Total Charts",
  "Created On",
  "Created By",
  "Updated On",
  "Details",
  "Action",
];

const tableCellSx = {
  padding: "8px 10px",
  paddingLeft: "15px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
};

const tableCellSx2 = {
  padding: "8px 8px 8px 40px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
};

const PlotTypeInfoTooltip = ({ data }) => {
  if (!data?.length) {
    return (
      <Typography sx={{ fontSize: 12, p: 1 }}>
        No chart information available
      </Typography>
    );
  }

  const formatChartType = (type = "") =>
    type
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

  return (
    <Box
      sx={{
        maxWidth: 340,
        width: "100%",
        p: "8px 4px",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          background: "#f3f4f6",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          p: 1,
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            maxHeight: 220,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {data.map((chart, i) => (
            <Box
              key={i}
              sx={{
                mb: 1,
                p: 1,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                {chart.chart_name}
              </Typography>

              <Divider sx={{ my: 0.5 }} />

              <Grid container spacing={0.5}>
                <Grid item xs={5} sx={{ minWidth: 0 }}>
                  <Typography sx={labelStyle}>Parameter</Typography>
                </Grid>

                <Grid item xs={7} sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...valueStyle, wordBreak: "break-word" }}>
                    {chart.parameter || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={5}>
                  <Typography sx={labelStyle}>Type</Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography sx={valueStyle}>
                    {formatChartType(chart.chart_type)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const Row = ({ row, handleMenuClick }) => {
  const [open, setOpen] = useState(false);

  const renderNameCell = (data) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box
        sx={{
          width: 26,
          display: "flex",
          justifyContent: "center",
          paddingRight: "8px",
        }}
      >
        {data?.is_favourite && (
          <StarIcon sx={{ fontSize: 18, color: "#f5b301" }} />
        )}
      </Box>

      <Label text={data?.viz_saved_name} fontSize={14} fontWeight={500} />

      {!data?.save_type && (
        <Chip
          label="Template"
          size="small"
          sx={{ ml: 1, height: 20, fontSize: 11 }}
        />
      )}
    </Box>
  );

  return (
    <>
      {/* MAIN ROW */}
      <TableRow hover>
        <TableCell sx={{ width: 40, p: "0px 8px" }}>
          {row?.versions?.length ? (
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ padding: 0 }}
            >
              {open ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 32 }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 32 }} />
              )}
            </IconButton>
          ) : null}
        </TableCell>

        <TableCell sx={{ ...tableCellSx, width: "30%" }}>
          {renderNameCell(row)}
        </TableCell>

        <TableCell sx={tableCellSx}>
          <Label text={row?.version_id} />
        </TableCell>

        <TableCell sx={tableCellSx}>
          <Label text={row?.count} />
        </TableCell>

        <TableCell sx={tableCellSx}>
          <Label text={row?.created_on} />
        </TableCell>

        <TableCell sx={tableCellSx}>
          <Label text={row.user_name} />
        </TableCell>

        <TableCell sx={tableCellSx}>
          <Label text={row?.updated_at} />
        </TableCell>

        <TableCell sx={tableCellSx}>
          <Tooltip
            arrow
            placement="top"
            title={<PlotTypeInfoTooltip data={row?.charts} />}
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>

        <TableCell sx={tableCellSx}>
          <IconButton size="small" onClick={(e) => handleMenuClick(e, row)}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* VERSION ROWS */}
      {open &&
        row?.versions?.map((version, i) => {
          const versionWithParent = {
            ...version,
            viz_id: row.viz_id, // ✅ inherit from parent
          };

          return (
            <TableRow
              key={i}
              sx={{ backgroundColor: "#F5F5F5", paddingLeft: 4 }}
            >
              <TableCell />

              <TableCell sx={tableCellSx2}>{renderNameCell(versionWithParent)}</TableCell>

              <TableCell sx={tableCellSx}>
                <Label text={version.version_id} />
              </TableCell>

              <TableCell sx={tableCellSx}>
                <Label text={version.count} />
              </TableCell>

              <TableCell sx={tableCellSx}>
                <Label text={version.created_on} />
              </TableCell>
              <TableCell sx={tableCellSx}></TableCell>

              <TableCell sx={tableCellSx}>
                <Label text={version.updated_at} />
              </TableCell>

              <TableCell sx={tableCellSx}>
                <Tooltip
                  arrow
                  placement="top"
                  title={<PlotTypeInfoTooltip data={version?.charts} />}
                >
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>

              <TableCell sx={tableCellSx}>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, versionWithParent)}
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          );
        })}
    </>
  );
};

const VisualizationsListTable = ({ reposrtsList }) => {
  const navigate = useNavigate();

  const { loader, apiError, message, data = [] } = reposrtsList || {};
  const result = data?.result;

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const open = Boolean(anchorEl);

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleView = () => {
    handleMenuClose();
    navigate(`/analytics/visualization/charts/view`, {
      state: {
        viz_id: selectedRow.viz_id,
        mode: "view",
        version_id: selectedRow.version_id,
      },
    });
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/analytics/visualization/charts/edit`, {
      state: {
        viz_id: selectedRow.viz_id,
        mode: "edit",
        version_id: selectedRow.version_id,
      },
    });
  };

  if (loader) {
    return (
      <Box py={6} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (apiError) {
    return (
      <Box py={6} display="flex" justifyContent="center">
        <Typography color="error">{message}</Typography>
      </Box>
    );
  }

  if (!result?.length) {
    return (
      <Box py={6} textAlign="center">
        <Label text="No Records found." />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} className="process-table-container">
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: "#E9E9E9" }}>
              {HEADERS.map((h, i) => (
                <TableCell key={i} className="table-header-cell">
                  <Label text={h} fontSize="14px" fontWeight="700" />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {result.map((row, i) => (
              <Row key={i} row={row} handleMenuClick={handleMenuClick} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        {/* {selectedRow?.save_type ? (
          <MenuItem onClick={handleView}>
            <Label text="View" />
          </MenuItem>
        ) : ( */}
        <MenuItem onClick={handleEdit}>
          <Label text="Open" />
        </MenuItem>
        {/* )} */}
      </Menu>
    </Box>
  );
};

export default VisualizationsListTable;
